/* Kendo-react-components wrappers to provide common functionality */

import { Calendar, DatePicker } from '@progress/kendo-react-dateinputs';
import { ComboBox, DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import { Input, NumericTextBox, Switch } from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonToolbar, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import Dropdown from 'react-dropdown';
import ReactQuill from 'react-quill';
import VirtualizedSelect from 'react-virtualized-select';

import { BootstrapColXl as Col } from '../bootstrap/bootstrapcolxl';
import SearchInput from '../searchInput';

const KendoInputWrapper = ({ children, ...rest }) => {
    const label = _.get(rest, 'label', '');
    const labelExample = _.get(rest, 'labelExample', '');
    const labelHelpTooltip = _.get(rest, 'labelHelpTooltip', '');
    const colInputXs = _.get(rest, 'colInputXs', 5);
    const colLabelXs = _.get(rest, 'colLabelXs', 7);
    const colInputMd = _.get(rest, 'colInputMd', colInputXs);
    const colLabelMd = _.get(rest, 'colLabelMd', colLabelXs);
    const colInputLg = _.get(rest, 'colInputLg', colInputXs);
    const colLabelLg = _.get(rest, 'colLabelLg', colLabelXs);
    const colInputXl = _.get(rest, 'colInputXl', colInputXs);
    const colLabelXl = _.get(rest, 'colLabelXl', colLabelXs);
    const disableWrapper = _.get(rest, 'disableWrapper', false);
    const rowClassName = _.get(rest, 'rowClassName', '');
    const horizontalWrapper = _.get(rest, 'horizontalWrapper', false);

    // console.log(`KendoInputWrapper`, { label, colInputXs, colLabelXs, colInputMd, colLabelMd, colInputLg, colLabelLg, colInputXl, colLabelXl, rest });

    const tooltip = (<Tooltip className="in" id={`tooltip-${labelHelpTooltip}`}>{labelHelpTooltip}</Tooltip>);
    const labelCaption = (labelHelpTooltip) ? (
        <OverlayTrigger
            overlay={tooltip}
            placement="right"
            delayShow={100}
            delayHide={0}
        >
            <span className="kendo-form-label-col-label-with-tooltip">{label}</span>
        </OverlayTrigger>
    ) : (
        <span>{label}</span>
    );

    const labelCol = (label) ? (
        <Col xs={colLabelXs} md={colLabelMd} lg={colLabelLg} xl={colLabelXl} className="kendo-form-label-col">
            <span className="kendo-form-label-col-label">
                {labelCaption}
                <span className="kendo-form-label-col-label-example">{labelExample}</span>
            </span>
        </Col>
    ) : <div />;

    if (horizontalWrapper) {
        return (
            <Row className={`kendo-form-row ${rowClassName}`}>
                {labelCol}
                <Col xs={colInputXs} md={colInputMd} lg={colInputLg} xl={colInputXl} className="kendo-form-input-col-horizontal">
                    {children}
                </Col>
            </Row>
        );
    }

    if (disableWrapper) {
        return (
            <div className="kendo-form-input-disabled-wrapper">
                {children}
            </div>
        );
    } else {
        return (
            <Row className={`kendo-form-row ${rowClassName}`}>
                {labelCol}
                <Col xs={colInputXs} md={colInputMd} lg={colInputLg} xl={colInputXl} className="kendo-form-input-col">{children}</Col>
            </Row>
        );
    }
};

const KendoInputProps = ({ ...rest }, castValueToString: boolean = true) => {
    let props = _.omit(rest, [
        'label',
        'labelExample',
        'labelHelpTooltip',
        'colLabelXs',
        'colInputXs',
        'colInputMd',
        'colLabelMd',
        'colInputLg',
        'colLabelLg',
        'colInputXl',
        'colLabelXl',
        'rowClassName',
        'disableWrapper',
        'horizontalWrapper'
    ]);

    return props;
};

export const KendoVirtualizedSelectInput = (rest) => {
    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <VirtualizedSelect
                {...inputProps}
            />
        </KendoInputWrapper>
    );
};

export const KendoGroupedDropdownInput = (rest) => {
    // Returns a react-dropdown input

    let inputProps = KendoInputProps(rest);

    inputProps.value = String(_.get(inputProps, 'value', ''));

    return (
        <KendoInputWrapper {...rest}>
            <Dropdown options={rest.options} {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoComboInputChildren = (restProps) => {
    // Returns a Kendo input for text
    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <ComboBox key={String(inputProps.value)} {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoComboInput = (rest) => {
    // Returns a Kendo input for text

    let inputProps = KendoInputProps(rest);

    inputProps.value = String(_.get(inputProps, 'value', ''));

    return (
        <KendoInputWrapper {...rest}>
            <ComboBox key={String(inputProps.value)} {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoDropdownListInputChildren = (restProps) => {
    // Returns a Kendo input for text
    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <DropDownList {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoDropdownListInput = (rest) => {
    // Returns a Kendo input for text

    let inputProps = KendoInputProps(rest);
    // console.log('KendoDropdownListInput', rest);
    // inputProps.value = String(_.get(inputProps, 'value', ''));

    return (
        <KendoInputWrapper {...rest}>
            <DropDownList {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoNumberInput = (rest) => {
    // Returns a Kendo input for numbers
    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <NumericTextBox {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoNumberInputWithChildren = ({ children, ...rest }) => {
    // Returns a Kendo input for numbers
    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <NumericTextBox {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoTextInputDebouncedChildren = ({ children, className = '', handleChange, ...rest }) => {
    // Returns a Kendo input for text

    let inputProps = KendoInputProps(rest);

    inputProps.value = String(_.get(inputProps, 'value', ''));
    inputProps.className = `${className} form-control k-textbox`;

    return (
        <KendoInputWrapper {...rest}>
            <SearchInput minLength={0} onChange={handleChange} {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoTextInputDebounced = ({ className = '', handleChange, ...rest }) => {
    // Returns a Kendo input for text

    let inputProps = KendoInputProps(rest);

    inputProps.value = String(_.get(inputProps, 'value', ''));
    inputProps.className = `${className} form-control k-textbox`;

    return (
        <KendoInputWrapper {...rest}>
            <SearchInput minLength={0} onChange={handleChange} {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoTextareaDebounced = ({ handleChange, ...rest }) => {
    // Returns a Kendo textarea control
    let inputProps = KendoInputProps(rest);

    inputProps.rows = _.get(rest, 'rows', 3);
    inputProps.cols = _.get(rest, 'cols', 40);

    return (
        <KendoInputWrapper {...rest}>
            <SearchInput element="textarea" minLength={0} onChange={handleChange} {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoHtmlEditor = (rest) => {
    // Returns a Kendo textarea control
    let inputProps = KendoInputProps(rest);

    return (
        <KendoInputWrapper {...rest}>
            <ReactQuill {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoTextInput = (restProps) => {
    // Returns a Kendo input for text
    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);

    inputProps.value = String(_.get(inputProps, 'value', ''));

    return (
        <KendoInputWrapper {...rest}>
            <Input {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoSingleYearInput = (rest) => {
    // Returns a Kendo input for integer single year

    let inputProps = KendoInputProps(rest);

    // inputProps.min = _.get(rest, 'min', 2000);
    // inputProps.max = _.get(rest, 'max', 2200);
    inputProps.step = _.get(rest, 'step', 1);
    // const v = Number(_.get(rest, 'value', inputProps.min));
    // console.log(`KendoSingleYearInput setting value to v ${v} rest.value: ${_.get(rest, 'value')}. inputProps:`, inputProps);
    inputProps.value = rest.value;
    inputProps.format = '####';

    return (
        <KendoInputWrapper {...rest}>
            <NumericTextBox {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoMonthsInput = (rest) => {
    const monthsText = _.get(rest, 'monthsText', 'Months');
    // Returns a Kendo input for periods aka months

    const addonSymbol: string = monthsText;

    let inputProps = KendoInputProps(rest);

    inputProps.min = _.get(rest, 'min', 1);
    inputProps.max = _.get(rest, 'max', 12);
    inputProps.step = _.get(rest, 'step', inputProps.min);
    inputProps.value = Number(_.get(rest, 'value', inputProps.min));
    inputProps.format = '##';

    // if (!_.get(rest, 'addonAfter') && addonSymbol) { inputProps.addonAfter = (<span>{addonSymbol}</span>); }

    return (
        <KendoInputWrapper {...rest}><NumericTextBox {...inputProps} /></KendoInputWrapper>
    );
};

export const KendoDisabledInputHidden = (rest) => {
    // Returns a Kendo input for disabled values

    const value = _.get(rest, 'value', '');
    const displayValue = _.get(rest, 'help', value);
    const className = _.get(rest, 'tableClassName', '');
    const { children } = rest;

    return (
        <KendoInputWrapper {...rest}>
            <div className={`kendo-input-value-only-table ${className}`}>
                <span className="kendo-input-value-only-value">{displayValue}{children}</span>
            </div>
        </KendoInputWrapper>
    );
};

export const KendoPercentageInput = ({ decimals = 7, minFracDigits = 3, ...rest }) => {
    // Returns a Kendo input for percentage values

    let inputProps = KendoInputProps(rest);

    inputProps.min = _.get(rest, 'min', 0.00);
    inputProps.max = _.get(rest, 'max', 100.00);
    inputProps.spinners = false;
    inputProps.value = Number(_.get(rest, 'value', inputProps.min));
    inputProps.format = { style: 'percent', minimumFractionDigits: minFracDigits, maximumFractionDigits: 3 };

    return (
        <KendoInputWrapper {...rest}><NumericTextBox {...inputProps} /></KendoInputWrapper>
    );
};

export const KendoCurrencyInputNew = ({ decimals = 2, ...restProps }) => {
    // Returns a Kendo input for currency decimal values

    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);

    // inputProps.min = _.get(rest, 'min', 0.00);
    // inputProps.step = _.get(rest, 'step', 0.01);
    // inputProps.defaultValue = inputProps.defaultValue; // Number(_.get(rest, 'value', inputProps.min));
    inputProps.spinners = false;
    inputProps.format = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };

    return (
        <KendoInputWrapper {...rest}>
            <NumericTextBox {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoCurrencyInput = ({ decimals = 2, ...restProps }) => {
    // Returns a Kendo input for currency decimal values

    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);

    inputProps.min = _.get(rest, 'min', 0.00);
    inputProps.spinners = false;
    inputProps.value = Number(_.get(rest, 'value', inputProps.min));
    inputProps.format = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };

    return (
        <KendoInputWrapper {...rest}>
            <NumericTextBox {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoDateYearInputOptional = ({ x = 1, ...restProps }) => {
    // Returns a Kendo input for year
    const { children, ...rest } = restProps;

    let inputProps = KendoInputProps(rest);
    inputProps.value = _.get(rest, 'value', new Date());
    inputProps.spinners = false;

    return (
        <KendoInputWrapper {...rest}>
            <Calendar {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoDateInput = (rest) => {
    // Returns a Kendo input for dates
    let inputProps = KendoInputProps(rest);

    inputProps.min = new Date(_.get(rest, 'min', '2000-01-01'));
    inputProps.max = new Date(_.get(rest, 'max', '2200-12-31'));
    inputProps.format = _.get(rest, 'format', 'M/dd/yyyy');
    inputProps.formatPlaceholder = _.get(rest, 'formatPlaceholder', 'formatPattern');
    inputProps.defaultValue = inputProps.value;
    inputProps.spinners = false;

    const { children } = rest;

    return (
        <KendoInputWrapper {...rest}>
            <DatePicker {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoDateInputOptional = (rest) => {
    // Returns a Kendo input for dates
    let inputProps = KendoInputProps(rest);

    inputProps.format = _.get(rest, 'format', 'M/dd/yyyy');

    inputProps.formatPlaceholder = _.get(rest, 'formatPlaceholder', 'formatPattern');
    inputProps.defaultValue = inputProps.value;

    const { children } = rest;
    // console.log('KendoDateInputOptional', { inputProps });

    return (
        <KendoInputWrapper {...rest}>
            <DatePicker {...inputProps} />
            {children}
        </KendoInputWrapper>
    );
};

export const KendoYearsInput = ({ yearsText = 'Years', ...rest }) => {
    // Returns a Kendo input for integer year counts
    const addonSymbol: string = yearsText;

    let inputProps = KendoInputProps(rest);

    inputProps.type = 'number';
    inputProps.min = _.get(rest, 'min', 0);
    inputProps.step = _.get(rest, 'step', 1);
    inputProps.spinners = false;

    if (!_.get(rest, 'addonAfter') && addonSymbol) { inputProps.addonAfter = (<span>{addonSymbol}</span>); }

    return (
        <KendoInputWrapper {...rest}><NumericTextBox {...inputProps} /></KendoInputWrapper>
    );
};

export const KendoMultiSelect = ({ ...rest }) => {
    // Returns a Kendo multiselect

    let inputProps = KendoInputProps(rest, false);
    // const value = (_.get(rest, 'value', false)) ? true : false;
    // _.unset(inputProps, 'value');

    // const value = (_.get(rest, 'value', false)) ? true : false;
    // inputProps.defaultChecked = value;
    // inputProps.checked = value;

    return (
        <KendoInputWrapper {...rest}>
            <MultiSelect {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoCheckbox = ({ ...rest }) => {
    // Returns a Kendo switch control

    let inputProps = KendoInputProps(rest, false);
    const value = (_.get(rest, 'value', false)) ? true : false;
    _.unset(inputProps, 'value');

    // const value = (_.get(rest, 'value', false)) ? true : false;
    // inputProps.defaultChecked = value;
    inputProps.checked = value;

    return (
        <KendoInputWrapper {...rest}>
            <Switch {...inputProps} />
        </KendoInputWrapper>
    );
};

export const KendoTextarea = ({ ...rest }) => {
    // Returns a Kendo textarea control
    let inputProps = KendoInputProps(rest);

    inputProps.rows = _.get(rest, 'rows', 3);
    inputProps.cols = _.get(rest, 'cols', 40);

    let { onChange, ...otherProps } = inputProps;

    return (
        <KendoInputWrapper {...rest}>
            <SearchInput element="textarea" onChange={onChange} {...otherProps} />
        </KendoInputWrapper>
    );
};

export const KendoVerticalForm = ({ children, ...rest }) => {
    // Returns a Kendo <Form> tag with default options
    // let inputProps = Object.assign({}, { layout: 'vertical' }, rest);

    return (
        <form {...rest}>
            {...children}
        </form>
    );
};

export const KendoHorizontalForm = ({ children, ...rest }) => {
    // Returns a Kendo <Form> tag with default options
    // let inputProps = Object.assign({}, { layout: 'horizontal' }, rest);

    return (
        <form className="____k-form-inine" {...rest}>
            {...children}
        </form>
    );
};

export const KendoFormSection = ({ title, children, ...rest }) => {
    // Returns a form section block
    let inputProps = Object.assign({}, rest);

    return (
        <div className="form-section" {...inputProps}>
            <div className="form-section-title">
                {title}
                {
                    (title) ? (<hr className="form-section-title-hr" />) : undefined
                }
            </div>
            {...children}
        </div>
    );
};

export const KendoButtonToolbar = ({ submitText, cancelText, onCancel, ...rest }) => {
    // Returns form button toolbar

    return (
        <ButtonToolbar {...rest}>
            <Button type="button" bsStyle="danger" onClick={onCancel}>{cancelText}</Button>
            <Button type="submit" bsStyle="success">{submitText}</Button>
        </ButtonToolbar>
    );
};

export const KendoFormSectionTitled = ({ title, className = '', ...rest }) => {
    return (
        <div className={`inventory-detail-form-section ${className}`}>
            <KendoFormSectionNoBorder title={title}>
                {rest.children}
            </KendoFormSectionNoBorder>
        </div>
    );
};

export const KendoFormSectionNoBorder = ({ title, children, ...rest }) => {
    // Returns a form section block
    // let inputProps = Object.assign({}, rest);

    return (
        <div className="kendo-form-section">
            <div className="kendo-form-section-title">
                {title}
                {
                    (title) ? (<hr className="kendo-form-section-title-hr" />) : undefined
                }
            </div>
            {...children}
        </div>
    );
};

export const KendoFormSectionSpacing = (rest) => {
    return (
        <div className={`kendo-form-section-spacing`} />
    );
};

export const ValidationErrorMessage = ({ defaultInputProps, children, ...rest }) => {
    return (
        <Row>
            <Col xs={defaultInputProps.colLabelXs} />
            <Col xs={defaultInputProps.colInputXs}>
                <div className="form-validation-error">{children}</div>
            </Col>
        </Row>
    );
};



// WEBPACK FOOTER //
// ./src/common/kendoforms/componentwrappers.tsx